import React, { useRef, useEffect } from "react";

const InputField = ({
    value,
    onChange,
    onBlur,
    name,
    label,
    labelFor,
    inputType = "text",
    placeholder,
    isRequired = false,
    isLabelLeft = false,
    isSocialLink,
    error,
    disabled,
    showRequiredIndicator = true, // New prop to control asterisk visibility
    className = "", // Additional className for input element
    maxLength, // Maximum length for input
}) => {
    const inputRef = useRef(null);
    const cursorPositionRef = useRef(null);

    // Preserve cursor position during value updates
    useEffect(() => {
        if (inputRef.current && cursorPositionRef.current !== null && document.activeElement === inputRef.current) {
            const input = inputRef.current;
            const cursorPos = cursorPositionRef.current;
            // Restore cursor position after React updates
            setTimeout(() => {
                if (document.activeElement === input) {
                    input.setSelectionRange(cursorPos, cursorPos);
                }
                cursorPositionRef.current = null;
            }, 0);
        }
    }, [value]);

    const handleChange = (e) => {
        // Browser already reports the correct caret position after the edit; use it for post-render restore
        const input = e.target;
        if (input.selectionStart !== null) {
            cursorPositionRef.current = input.selectionStart;
        }
        if (onChange) {
            onChange(e);
        }
    };

    const handleFocus = (e) => {
        // If cursor is at start when field has content, move to end
        const input = e.target;
        if (input && input.value && input.value.length > 0) {
            setTimeout(() => {
                if (document.activeElement === input && input.selectionStart === 0 && input.selectionEnd === 0) {
                    // Cursor is at start, move to end
                    input.setSelectionRange(input.value.length, input.value.length);
                }
            }, 0);
        }
    };

    return (
        <div className={`${isLabelLeft ? "flex items-center gap-4" : "mb-4"}`}>
            {label && (
                <label
                    htmlFor={labelFor}
                    className={`text-sm text-fontColor dark:text-[#c9d1d9] ${
                        isSocialLink ? "font-normal" : "font-bold"
                    } ${isLabelLeft ? "min-w-[70px] whitespace-nowrap" : "block mb-2"}`}
                >
                    {label} {isRequired && showRequiredIndicator && <span className="text-red-500 dark:text-red-400">*</span>}
                </label>
            )}
            <div className="relative w-full">
                <input
                    ref={inputRef}
                    id={labelFor}
                    name={name}
                    value={inputType === "date" ? (value || '') : (inputType === "number" && value < 0 ? 0 : (value ?? ''))}
                    onChange={handleChange}
                    onBlur={onBlur}
                    onFocus={handleFocus}
                    type={inputType}
                    min={inputType === "number" ? "0" : undefined}
                    maxLength={maxLength}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={`px-4 py-2 w-full bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9] placeholder:text-[14px] placeholder:font-normal placeholder:text-gray-400 dark:placeholder:text-[#6e7681] text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 focus:border-transparent transition-colors ${
                        error ? "border-red-500 dark:border-red-400" : "border-gray-300 dark:border-[#30363d]"
                    } ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${isLabelLeft ? "w-[200px] 867:w-[286px]" : "w-full"} ${className}`}
                />
                {error && (
                    <p className="text-red-500 dark:text-red-400 text-xs mt-1 font-medium">{error}</p>
                )}
            </div>
        </div>
    );
};

export default InputField;
